import { apiInstance } from '@/api/instance';
import { type User } from '@/types/user';

export interface SigninRequest {
  email: string;
  password: string;
}

interface SigninResponse {
  user: User;
}

type UserSource = 'main_web' | 'main_app' | 'partners_web';

/**
 * Same endpoint as bodyprint-web. The portal always sends `source: 'partners_web'`, so the API
 * points the activation and reset links at the portal. Birth date and gender are optional in the
 * API and not asked here (the partner account carries the organization data).
 */
export type SignupRequest = Pick<User, 'email' | 'firstName' | 'lastName'> & {
  password: string;
  receivePlatformUpdateEmails: boolean;
  /** The privacy policy box; the API refuses a sign-up without it (`TERMS_REQUIRED`). */
  acceptedTerms: true;
  source: UserSource;
};

export type ActivateAccountResponse = Record<string, never>;

export type ForgotPasswordRequest = Pick<User, 'email'>;

export type ResetPasswordRequest = {
  token: string;
  newPassword: string;
};

export type ActivateAccountRequest = {
  token: string;
};

export type ResendAccountActivationRequest = Pick<ActivateAccountRequest, 'token'>;

export const authApi = {
  signup: (data: SignupRequest) => apiInstance.post<void>('auth/signup', data),
  signin: (data: SigninRequest) => apiInstance.post<SigninResponse>('auth/signin', data),
  forgotPassword: (data: ForgotPasswordRequest) =>
    apiInstance.post<void>('auth/password/reset', data),
  resetPassword: (data: ResetPasswordRequest) =>
    apiInstance.post<void>('auth/password/reset/confirm', data),
  activateAccount: (data: ActivateAccountRequest) =>
    apiInstance.post<ActivateAccountResponse>('auth/activate', data),
  resendAccountActivation: (data: ResendAccountActivationRequest) =>
    apiInstance.post<void>('auth/activate/resend', data),
};
